import { createReducer } from '@reduxjs/toolkit';
import { SiteDto, IPurchaseOrderDto, PurchaseOrderDto, ClientDto, SpecializedOrderStatus } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyPurchaseOrder,
    createNewPurchaseOrder,
    clearPurchaseOrder,
    savePurchaseOrder,
    importPurchaseOrder,
    removePurchaseOrderDestinationSite,
    setPurchaseOrderDestinationSite,
    updatePurchaseOrderData,
    updatePurchaseOrderDescription,
    updatePurchaseOrderBatchArrivalTime,
    updatePurchaseOrderUniqueBatchId,
    setPurchaseOrderSupplier,
    removePurchaseOrderSupplier,
    changePurchaseOrderStatus,
    updatePurchaseOrderDocumentCode,
} from '../actions/purchaseOrderActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';
import { saveReceptionOrderFromPurchaseOrder } from '../actions/orderActions';

export interface PurchaseOrderFormReducerState extends IBaseEntityState {
    purchaseOrder: IPurchaseOrderDto | null;
    uniqueBatchId: string | null;
    batchArrivalTime: Date | null;
}

function clearState(state: PurchaseOrderFormReducerState): void {
    state.purchaseOrder = null;
    state.uniqueBatchId = null;
    state.batchArrivalTime = null;

    clearBaseEntityState(state);
}

function getInitialState(): PurchaseOrderFormReducerState {
    const state: PurchaseOrderFormReducerState = {
        purchaseOrder: null,
        uniqueBatchId: null,
        batchArrivalTime: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: PurchaseOrderFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.purchaseOrder != null, 'Need order to calculate');

    const jsonBaseline = JSON.stringify(state.purchaseOrder);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: PurchaseOrderFormReducerState) {
    myAssert(() => state.purchaseOrder != null, 'Need order to calculate');

    if (!state.purchaseOrder!.id || state.purchaseOrder!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of order
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.purchaseOrder, undefined);

        state.modified =
            (modified ?? state.purchaseOrder!.created)
                ? {
                      modifiedAt: state.purchaseOrder!.created ?? new Date(),
                      modifiedBy: state.purchaseOrder!.createdBy ?? '',
                  }
                : undefined;
    }
}

export const purchaseOrderFormReducer = createReducer<PurchaseOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyPurchaseOrder, (state: PurchaseOrderFormReducerState, action: any) => {
                clearState(state);

                const newOrder: IPurchaseOrderDto = new PurchaseOrderDto();

                newOrder.id = 0;
                newOrder.purchaseOrderStatus = SpecializedOrderStatus.Created;

                state.purchaseOrder = newOrder;
                state.uniqueBatchId = '/';

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewPurchaseOrder, (state: PurchaseOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IPurchaseOrderDto = action.payload;

                // check order data
                myAssert(() => payload.id === 0, 'It is new order!!!');

                state.purchaseOrder = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importPurchaseOrder.fulfilled, (state: PurchaseOrderFormReducerState, action: any) => {
                clearState(state);

                const payload: IPurchaseOrderDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.purchaseOrder = payload;
                state.batchArrivalTime = new Date();
                state.uniqueBatchId =
                    !!payload.goods && !!payload.goods[0] ? (payload.goods[0].batch?.uniqueBatchId ?? null) : null;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearPurchaseOrder, (state: PurchaseOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(setPurchaseOrderDestinationSite, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                const site = action.payload as SiteDto;

                state.purchaseOrder! = { ...state.purchaseOrder, destinationSite: site, destinationSiteId: site?.id };

                calculateCurrentJson(state);
            })
            .addCase(removePurchaseOrderDestinationSite, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.purchaseOrder! = {
                    ...state.purchaseOrder,
                    destinationSite: undefined,
                    destinationSiteId: undefined,
                };

                calculateCurrentJson(state);
            })
            .addCase(setPurchaseOrderSupplier, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                const supplier = action.payload as ClientDto;

                state.purchaseOrder! = {
                    ...state.purchaseOrder,
                    supplier: supplier,
                    supplierId: supplier.id,
                };

                calculateCurrentJson(state);
            })
            .addCase(removePurchaseOrderSupplier, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.purchaseOrder! = {
                    ...state.purchaseOrder,
                    supplier: undefined,
                    supplierId: undefined,
                };

                calculateCurrentJson(state);
            })
            .addCase(updatePurchaseOrderDescription, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.purchaseOrder! = { ...state.purchaseOrder, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updatePurchaseOrderBatchArrivalTime, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.batchArrivalTime = action.payload ?? null;
                calculateCurrentJson(state);
            })
            .addCase(updatePurchaseOrderUniqueBatchId, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.uniqueBatchId = action.payload ?? null;
                calculateCurrentJson(state);
            })
            .addCase(updatePurchaseOrderDocumentCode, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.purchaseOrder! = { ...state.purchaseOrder, documentCode: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updatePurchaseOrderData, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if it is being updated');

                state.purchaseOrder! = { ...state.purchaseOrder, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(savePurchaseOrder.pending, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(savePurchaseOrder.fulfilled, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(savePurchaseOrder.rejected, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(changePurchaseOrderStatus.pending, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(changePurchaseOrderStatus.fulfilled, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(changePurchaseOrderStatus.rejected, (state: PurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(
                saveReceptionOrderFromPurchaseOrder.pending,
                (state: PurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                    myAssert(
                        () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                        '',
                        false,
                    );

                    state.saveStatus = SaveEntityStatus.Pending;
                },
            )
            .addCase(
                saveReceptionOrderFromPurchaseOrder.fulfilled,
                (state: PurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                    myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                    state.saveStatus = SaveEntityStatus.Saved;

                    calculateBaseInfo(state);
                    calculateCurrentJson(state, true);
                },
            )
            .addCase(
                saveReceptionOrderFromPurchaseOrder.rejected,
                (state: PurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.purchaseOrder !== null, 'There needs to be order if save is on its way');
                    myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                    state.saveStatus = SaveEntityStatus.Error;
                },
            );
    },
);
